// @ts-strict-ignore
import { forwardRef, Inject, Injectable, NgZone } from '@angular/core';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { AppConstants, LMFormType, LMTransformOptions } from '@utils/app.constants';
import { LMFormActionUtilService } from '@form/services/lm-form-action-util.service';
import { LMFormService } from '@form/services/lm-form.service';
import { LMContextService } from '@services/lm-context.service';
import { LMFieldUtilService } from '@services/utils/lm-field-util.service';
import { LMActionService } from '@services/lm-action.service';
import { LMModelField } from './lm-model-field';
import { LMModel } from './lm-model';
import { SafeUrl } from '@angular/platform-browser';
import { trim } from 'lodash-es';
import { LMFormSpecUtilService } from '@form/services/lm-form-spec-util.service';

@Injectable()
export class LMModelServiceAdapter {

  private _modelActionState: LMModelActionState;
  private lmFormatUtilService;
  private isCreateReportMode = false;

  constructor(
    public lmLoggerService: LMLoggerService,
    private lmActionService: LMActionService,
    private lmFieldUtilService: LMFieldUtilService,
    @Inject(forwardRef(() => LMFormatUtilService)) lmFormatUtilService, // LMFormatUtilService
    private lmFormActionUtilService: LMFormActionUtilService,
    private lmFormService: LMFormService,
    private lmFormSpecUtilService: LMFormSpecUtilService,
    private lmContextService: LMContextService,
    public ngZone: NgZone
  ) {
    this.lmFormatUtilService = lmFormatUtilService as LMFormatUtilService;

    const actionInfo: LMModelActionInfo = {
      isMutableAction:    false,
      isCreateAction:     false,
      isImportAction:     false,
      isUpdateAction:     false,
      isActionFormDialog: false
    };

    // initially populate with default settings
    this._modelActionState = {
      actionInfo,
      isFormMutable: false,
      isRequired:    null     // tri-state: null, false, true
    };

    this.lmFormService.isCreateReportMode$.subscribe(state => {
      this.isCreateReportMode = state;
    });
  }

  public getFormName(formSpec: LMFormSpec): string {
    return this.lmFormSpecUtilService.formName(formSpec);
  }

  public get modelActionState(): LMModelActionState {
    return this._modelActionState;
  }

  public get isFormMutable(): boolean {
    return this._modelActionState.isFormMutable;
  }

  public isReadOnlyForm(formSpec: LMFormSpec): boolean {
    return this.lmFormSpecUtilService.isReadOnlyForm(formSpec);
  }

  public isPageContextForm(formSpec: LMFormSpec): boolean {
    return this.lmFormSpecUtilService.isPageContextForm(formSpec);
  }

  public isSearchForm(formSpec: LMFormSpec): boolean {
    return this.lmFormSpecUtilService.isSearchForm(formSpec);
  }

  public isCubeSearchForm(formSpec: LMFormSpec): boolean {
    return this.lmFormSpecUtilService.isCubeSearchForm(formSpec);
  }

  public isDetailFieldsForm(formSpec: LMFormSpec): boolean {
    return this.lmFormSpecUtilService.isDetailFieldsForm(formSpec);
  }

  // TODO: temporary; these need to be on busFormSpec via LMFormService
  private get isDetailSection(): boolean {
    return false;
  }

  private get isScheduleForm(): boolean {
    return false;
  }

  private isTemplateTarget(formSpec: LMFormSpec): boolean {
    return this.lmFormSpecUtilService.isTemplateTarget(formSpec);
  }

  // public methods
  public isBlank(type: string, value: any) {
    return this.lmFieldUtilService.isBlank(type, value);
  }

  public isCompoundField(dvField: LMField, isMultiSelect: boolean) {
    return this.lmFormatUtilService.isCompoundField(dvField, isMultiSelect);
  }

  public formatMessage(message: string, variables?: any) {
    return this.lmFormatUtilService.formatMessage(message, variables);
  }

  public toBoolean(value: any): boolean {
    return this.lmFormatUtilService.booleanToData(value);
  }

  /**
   * Transforms localized data into Landmark data
   * @param value
   * @param type
   * @param options - optional
   * @returns
   */
  public toDataValue(value: any, type: string, options?: LMTransformOptions): any {
    return this.lmFormatUtilService.toDataValue(value, type, options);
  }

  /**
   *
   * @param value
   * @param specField
   * @param options
   * @return
   */
  public toDisplayValue(value: any, specField: LMSpecField, options?: LMTransformOptions): any {
    return this.lmFormatUtilService.toDisplayValueWithSpec(value, specField, options);
  }

  public toImageUrlValue(value: any): SafeUrl {
    return this.lmFormatUtilService.toImageUrlValue(value);
  }

  public fieldStatesToMultiOptionsArray(stateValues: Array<any>, values: Array<any>): Array<LMStateValue> {
    return this.lmFormatUtilService.fieldStatesToMultiOptionsArray(stateValues, values);
  }

  public fieldStatesToOptionsArray(stateValues: Array<any>, addBlankEntry: boolean, zeroOrBlankValue?: string): Array<LMStateValue> {
    return this.lmFormatUtilService.fieldStatesToOptionsArray(stateValues, addBlankEntry, zeroOrBlankValue);
  }

  public fieldStateFormatZeroValue(type: string, precision?: number): string {
    return this.lmFormatUtilService.fieldStateFormatZeroValue(type, precision);
  }

  /*public addEmptyStateItem(statesArray: Array<LMStateValue>, isSearchForm: boolean): Array<LMStateValue> {
    // add empty entry if a search form and one does not exist
    if (isSearchForm
      && statesArray.length > 0
      && statesArray[ 0 ].label.trim().length > 0) {

      // create a new Array; leave incoming Array as is
      const newStates: Array<LMStateValue> = [];

      // add empty entry to be first item
      newStates.push({ label: ' ', value: ' ' });

      for (let index = 0, len = statesArray.length; index < len; index++) {
        newStates.push(statesArray[ index ]);
      }

      return newStates;
    }

    return statesArray;
  }*/

  public booleanSearchStates(): Array<LMStateValue> {
    const states: Array<LMStateValue> = [];

    // add empty entry to be first item
    states.push({ label: ' ', value: ' ' });
    states.push({ label: Soho.Locale.translate('Yes'), value: 'true' });
    states.push({ label: Soho.Locale.translate('No'), value: 'false' });

    return states;
  }

  public updateActionOnDataView(currentDataView: LMDataView, newDataView: LMDataView, formSpec: LMFormSpec): LMDataView {
    // always returns the new dataView
    const newActionSpec: LMActionSpec = this.lmFormActionUtilService.getTargetAction(formSpec, newDataView);

    if (!currentDataView) {
      newDataView.action = (newActionSpec) ? newActionSpec.name : undefined;
      return newDataView;
    }

    if (!newActionSpec) {
      // if no actionSpec just return with the new DataView
      return newDataView;
    }

    const currentActionSpec: LMActionSpec = this.lmFormActionUtilService.getTargetAction(formSpec, currentDataView);
    const isInstanceOrDelete = (newActionSpec.type === AppConstants.ACTION_TYPE_FUNCTION || newActionSpec.type === AppConstants.ACTION_TYPE_DELETE);

    // if current dataView action is update and new dataView action is instance or delete,
    // continue to check if the new dataView action should be replaced with the current dataView action
    if (currentActionSpec && currentActionSpec.type === AppConstants.ACTION_TYPE_UPDATE) {
      if (isInstanceOrDelete) {

        // is current action valid
        const isValid: boolean = this.lmActionService.isValidAction(currentActionSpec, formSpec.busClassSpec.actions, currentDataView.actionIndexes, undefined, true, true);

        if (isValid) {
          // use current action if valid
          newDataView.action = currentDataView.action;
        } else {
          // otherwise, find new valid update action
          const updateActionSpec = this.lmActionService.getDefaultUpdateAction(formSpec.busClassSpec.actions, newDataView.actionIndexes, true);

          if (updateActionSpec) {
            newDataView.action = updateActionSpec.name;
          }
        }
      }
    }

    return newDataView;
  }

  public toBlankValue = (type: string) => {
    if (type === AppConstants.YYYYMMDD
      || type === AppConstants.YYYYMM
      || type === AppConstants.MMDD
      || type === AppConstants.YYYY
      || type === AppConstants.TIME
      || type === AppConstants.TIMESTAMP
      || type === AppConstants.NUMERIC
      || type === AppConstants.BCD
      || type === AppConstants.PERCENT) {
      return 0;
    }
    return '';
  };

  public parseGroupFieldMembers(modelField: LMModelField, model: LMModel): void {
    this.parseGroupFieldMemberValue(modelField.value, modelField, model);
  }

  public updateModelActionState(dataView: LMDataView, formSpec: LMFormSpec) {
    const actionSpec: LMActionSpec = this.lmActionService.getTargetAction({ formSpec, dataView });

    const actionInfo: LMModelActionInfo = this.getActionInfo(actionSpec);
    const isFormMutable: boolean = this.getFormMutable(dataView, formSpec, actionSpec);
    const isRequired: boolean = this.getRequired(isFormMutable);

    this._modelActionState = {
      actionInfo,
      isFormMutable,
      isRequired
    };
  }

  public getMutableState(specField: LMSpecField, dvField: LMField): boolean {
    // see this.getActionInfo()
    // isMutableAction    - true = action is create or action has parameters (symbolic key fields are mutable)
    // isUpdateAction     - true = action is update
    // isActionFormDialog - true = action has parameters or has action form

    if (!this._modelActionState.isFormMutable) {
      // disabled: form is not mutable
      return false;
    }

    if (this._modelActionState.actionInfo.isMutableAction) {
      if (specField.isContextKey
        && !dvField.mutable
        && this.isBlank(specField.type, dvField.value)) {
        // enabled: is a mutable action and is a context key and is not mutable and value is blank
        return true;
      }
    } else {
      if (specField.isSymbolicKey || specField.isPartOfRelativeKey) {
        // disabled: not a mutable action and is symbolic key or is part of relative key
        return false;
      }

      // TODO: need references
      // if (isBusinessClass({ fieldId: fieldId })) {
      //   // disabled: not a mutable action and is a business class
      //   return false;
      // }
    }

    if (this._modelActionState.actionInfo.isUpdateAction) {
      if (!this._modelActionState.actionInfo.isActionFormDialog
        && specField.isContextKey
        && !this.isBlank(specField.type, dvField.value)) {
        // disabled: is an update action and not a dialog and is not a context key or context field and value is not blank
        return false;
      }
    }

    // use value of mutable property
    return dvField.mutable;
  }

  // private methods
  private getActionInfo(actionSpec: LMActionSpec): LMModelActionInfo {
    // let formSpec: LMFormSpec = this.lmFormService.formSpec;
    // let actionSpec: LMActionSpec = this.lmFormActionUtilService.getTargetAction(formSpec, dataView, formSpec.action);

    // set return with initial values
    const data: LMModelActionInfo = {
      isMutableAction:    true,
      isCreateAction:     false,
      isImportAction:     false,
      isUpdateAction:     false,
      isActionFormDialog: false
    };

    if (actionSpec) {
      data.isMutableAction = (actionSpec.type === AppConstants.ACTION_TYPE_CREATE || actionSpec.type === AppConstants.ACTION_TYPE_IMPORT);
      data.isCreateAction = (actionSpec.type === AppConstants.ACTION_TYPE_CREATE);
      data.isUpdateAction = (actionSpec.type === AppConstants.ACTION_TYPE_UPDATE);
      data.isImportAction = (actionSpec.type === AppConstants.ACTION_TYPE_IMPORT);

      // if not a create action, see if there are parameters
      if (!data.isMutableAction) {
        if (actionSpec.hasParameters) {
          data.isMutableAction = true;
        }
      }

      if (actionSpec.hasParameters !== undefined
        || actionSpec.hasActionForm !== undefined) {
        data.isActionFormDialog = true;
      }
    }

    // isMutableAction    - true = create action or has parameters (symbolic key fields are mutable)
    // isUpdateAction     - true = update action
    // isActionFormDialog - true = has parameters or has action form
    return data;
  }

  private getFormMutable(dataView: LMDataView, formSpec: LMFormSpec, actionSpec: LMActionSpec): boolean {
    if (this.isCreateReportMode) {
      return false;
    }

    // LMContext.asOfDateInPast
    if (this.lmContextService.hasAsOfDateInPast()) {
      return false;
    }

    // set by FormControllerAction
    if (dataView.asOfDate) {
      return false;
    }

    if (this.lmContextService.isArchiveMode()) {
      return false;
    }

    // set by JsonConverter
    if (dataView.displayOnly) {
      return false;
    }

    // TODO: needs to be on busFormSpec
    if (this.isScheduleForm) {
      return true;
    }
    //     if (parentWidget.isScheduleForm) {
    //       return true;
    //     }

    if (this.isTemplateTarget(formSpec)) {
      return true;
    }

    if (this.isSearchForm(formSpec)) {
      return true;
    }

    // TODO: needs to be on busFormSpec
    if (this.isDetailSection) {
      return false;
    }
    //     if (parentWidget.isDetailSection) {
    //       return false;
    //     }

    if (this.isDetailFieldsForm(formSpec)) {
      return true;
    }

    // LMCLIENT-27632: action forms that do not have a valid target action need to display fields as readonly
    // in 3.5, this same evaluation is always undefined (because it was incorrect), so processing fell through to the
    // end where _getTargetAction() was evaluated;
    // in 4.0, the evaluation is correct and thus also needs to evaluate for getTargetAction() as evaluating actionSpec
    // alone is not enough
    if (formSpec.busFormSpec.formType === LMFormType.ACTION) {
      const action = this.lmFormActionUtilService.getTargetAction(formSpec, dataView, formSpec.action);
      return (action !== null);
    }

    if (this.lmFormSpecUtilService.isResponseRequired(formSpec)) {
      return true;
    }

    if (this.lmFormSpecUtilService.isCodeBlockFilter(formSpec)) {
      return true;
    }

    // let actionSpec: LMActionSpec = this.lmFormActionUtilService.getTargetAction(formSpec, dataView, formSpec.action);
    //     var action = parentWidget._getTargetAction(dataView);
    return (actionSpec !== null);
  };

  // TODO: TECHNICAL DEBT: consider renaming this method to be more respective of what it is doing/meaning
  private getRequired(isFormMutable: boolean) {
    // TODO: this.isDetailSection needs to be on busFormSpec

    return ((!this.isDetailSection || (this.isDetailSection && isFormMutable)));
  };

  // ported from landmark.field.setMemberFieldValue
  private parseGroupFieldMemberValue(compactValue: string, modelField: LMModelField, model: LMModel): string {
    const delimiter = modelField.groupFieldDelimiter || '';
    const members = modelField.members;

    for (let i = 0, length = members.length; i < length; i++) {
      if (compactValue && compactValue.length === 0) {

        // Clear out the rest of the members in the model
        for (let j = i; j < length; j++) {
          model.getField(members[ j ]).setBlank();
        }
        // Break when compactValue is empty string
        break;
      }

      const memberFieldId = members[ i ];
      const memberField = model.getField(memberFieldId);

      if (memberField.isCompoundField) {
        compactValue = this.parseGroupFieldMemberValue(compactValue, memberField, model);

      } else {
        if (delimiter.length > 0) {
          const index = compactValue.indexOf(delimiter);
          const newValue = ((index === -1) ? compactValue : compactValue.substr(0, index));

          // need to set individual member with dirty state; comparing data values, need to transform
          // the original value to string as the compactValue is always a string
          model.setDirty(memberFieldId, (memberField.originalValue.toString() !== newValue));

          memberField.value = newValue;
          compactValue = (index === -1) ? '' : compactValue.substr(index + 1);

        } else {
          const newValue = (compactValue.substr(0, memberField.size));

          // need to set individual member with dirty state; comparing data values, need to transform
          // the original value to string as the compactValue is always a string
          model.setDirty(memberFieldId, (memberField.originalValue.toString() !== newValue));

          memberField.value = newValue;
          compactValue = compactValue.substr(memberField.size);
        }
      }
    }

    return compactValue;
  }

  public computeCompactFieldValue(dataView: LMDataView, fieldId: string, specFields: LMSpecFields): string {
    const dvField: LMField = dataView.fields[ fieldId ];
    return this._computeCompactFieldValue(dataView, dvField, specFields, true);
  }

  // NOTE: method is recursive
  private _computeCompactFieldValue(dataView: LMDataView, dvField: LMField, specFields: LMSpecFields, checkBlank: boolean): string {
    let delimiter = dvField.delimiter || '';
    let returnValue = '';
    let addDelimiter = false;
    let allFieldsBlank = true;

    dvField.members.forEach((item) => {
      const memberField: LMField = dataView.fields[ item ];
      const type = memberField.type || '';

      if (addDelimiter) {
        returnValue += delimiter;
      }

      if (type === 'array' || type === 'group') {
        delimiter = memberField.delimiter || '';
        addDelimiter = (delimiter.length > 0);

        memberField.value = this._computeCompactFieldValue(dataView, memberField, specFields, false);

        allFieldsBlank = (allFieldsBlank && memberField.value.length === 0);
        returnValue += memberField.value;

      } else {
        const value = memberField.value;

        if (specFields.hasOwnProperty(item)) {
          const specField: LMSpecField = specFields[ item ];

          if (specField.type === AppConstants.BINARYDOCUMENT) {
            // TODO: issue: check if linkValue is still used for 4.0
            // value = memberField.linkValue;
          }

          allFieldsBlank = (allFieldsBlank && this.lmFieldUtilService.isBlank(specField.type, value));
        }

        if (delimiter.length === 0) {
          returnValue += this.getFieldPaddedValue(memberField);

        } else {
          addDelimiter = true;
          returnValue += trim(value.toString());
        }
      }
    });

    if (specFields && allFieldsBlank && checkBlank) {
      return '';
    }

    // trim trailing delimiters
    let newLength = returnValue.length;
    const delimLength = delimiter.length;

    for (; newLength > 0; newLength -= delimLength) {
      if (delimiter !== (returnValue.substring(newLength - delimLength, newLength))) {
        break;
      }
    }

    returnValue = returnValue.substr(0, newLength);
    return returnValue;
  }

  private getFieldPaddedValue(dvField: LMField): string {
    let value = dvField.value.toString();
    const length = value.length;

    if (length < dvField.size) {
      for (let index = length; index < dvField.size; index++) {
        value += ' ';
      }

    } else {
      value = value.substr(0, dvField.size);
    }

    return value;
  }
}
